import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { alpha } from "@mui/system";
import { styled } from "@mui/material/styles";
import { getImportItemById } from "../../../services/Import";
import InputField from "../../../FormComponents/InputField";
import BillWritableTable from "../../../components/BillWritableTable";
import FormAlert from "../../../components/FormAlert";
import PersonIcon from "@mui/icons-material/Person";
import SubPersonSelectingPopup from "../../../components/SubPersonSelectingPopup";
import { getSubOrderPersonById } from "../../../services/SubOrderPerson";
import { addSubOrder } from "../../../services/SubOrder";
import SubProductSelectingPopup from "../../../components/SubProductSelectingPopup";
import { getSubOrderProductById } from "../../../services/SubOrderProduct";
import { getCurrentPrice } from "../../../services/ImportPrice";
import MissingPriceWarning from "../../../components/MissingPriceWarning";
import ItemSelectingPopup from "../../../components/ItemSelectingPopup";
import SubOrderValidation from "../../../Validation/SubOrderValidation";

const SelectButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  minWidth: "20%",
  justifyContent: "flex-start",
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.2),
    borderColor: theme.palette.primary.main,
  },
}));

const CostDisplayBox = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.info.light, 0.08),
  border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.5, 2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

function AddSubOrder({ setOpenForm }) {
  const [values, setValues] = useState({
    _subOrderPersonId: "",
    _cost: 0.0,
    _labourCost: 0.0,
    _transportCost: 0.0,
    _paidAmount: 0.0,
    _subOrderItemBodyList: [],
    _note: "",
  });

  const [updateMaterialPrice, setUpdateMaterialPrice] = useState(true);

  const [addedDate] = useState(dayjs());
  const [selectedOrderDate, setSelectedOrderDate] = useState(dayjs());

  // Separate state for computed cost components
  const [materialCost, setMaterialCost] = useState(0.0);
  // importId -> unit price (null means price unavailable)
  const [inputItemPrices, setInputItemPrices] = useState({});
  const [missingPriceItems, setMissingPriceItems] = useState([]);

  const [originalInputQties, setOriginalInputQties] = useState([]);

  const resetValues = () => {
    closeAlert();
    setSelectedInputItems([]);
    setSelectedInputRows([]);
    setSelectedOutputRows([]);
    setOriginalInputQties([]);
    setSubOrderPersonData(null);
    setSelectedPersons([]);
    setError({});
    setMaterialCost(0.0);
    setInputItemPrices({});
    setMissingPriceItems([]);
    setUpdateMaterialPrice(true);
    setSelectedOrderDate(dayjs());
    setValues({
      _subOrderPersonId: "",
      _cost: 0.0,
      _labourCost: 0.0,
      _transportCost: 0.0,
      _paidAmount: 0.0,
      _subOrderItemBodyList: [],
      _note: "",
    });
  };

  // Alerts
  const [displayAlert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({ type: "", message: "" });

  const closeAlert = () => {
    setAlert(false);
    setAlertData({ type: "", message: "" });
  };

  const [openInputTable, setOpenInputTable] = useState(false);
  const [error, setError] = useState({});

  // Category item selecting popup — opened from a category row's icon
  const [openCategoryItemSelect, setOpenCategoryItemSelect] = useState(false);
  const [activeCategoryRow, setActiveCategoryRow] = useState(null); // { catId, catName, forType }
  const [categoryPopupSelectedItems, setCategoryPopupSelectedItems] = useState([]);
  const [categoryPopupRows, setCategoryPopupRows] = useState([]);

  const handleOpenCategoryItemSelect = (catId, catName, forType) => {
    const existingRows = forType === "INPUT" ? selectedInputRows : selectedOutputRows;
    const preSelected = existingRows
      .filter((r) => r.fromCategoryId === catId)
      .map((r) => r.importId);
    setCategoryPopupSelectedItems(preSelected);
    setActiveCategoryRow({ catId, catName, forType });
    setOpenCategoryItemSelect(true);
  };

  const handleCloseCategoryItemSelect = async () => {
    setOpenCategoryItemSelect(false);

    const forType = activeCategoryRow.forType;
    const catId = activeCategoryRow.catId;
    const catName = activeCategoryRow.catName;
    const selectedSet = new Set(categoryPopupSelectedItems);

    // Start by removing items that belonged to this category but were deselected
    let newInputRows = forType === "INPUT"
      ? selectedInputRows.filter((r) => r.fromCategoryId !== catId || selectedSet.has(r.importId))
      : [...selectedInputRows];
    let newOutputRows = forType === "OUTPUT"
      ? selectedOutputRows.filter((r) => r.fromCategoryId !== catId || selectedSet.has(r.importId))
      : [...selectedOutputRows];

    const newPrices = { ...inputItemPrices };
    const newMissingItems = [...missingPriceItems];

    for (const importId of categoryPopupSelectedItems) {
      // Skip if already present in either list
      if (
        newInputRows.some((r) => r.importId === importId) ||
        newOutputRows.some((r) => r.importId === importId)
      ) continue;

      const importItem = await getImportItemById(importId);

      const itemLabel = (
        <Box display="flex" alignItems="center" gap={0.5}>
          {importItem.itemName + " (" + importItem.product_code + ")"}
          <Chip
            size="small"
            label={catName}
            color="secondary"
            variant="outlined"
            sx={{ height: 16, fontSize: "0.65rem", "& .MuiChip-label": { px: 0.5 } }}
          />
        </Box>
      );

      if (forType === "INPUT") {
        const priceData = await getCurrentPrice(importItem.importId);
        if (priceData && priceData !== "Something went wrong..." && priceData.price != null) {
          newPrices[importItem.importId] = priceData.price;
        } else {
          newPrices[importItem.importId] = null;
          if (!newMissingItems.some((m) => m.importId === importItem.importId)) {
            newMissingItems.push({
              name: importItem.itemName + " (" + importItem.product_code + ")",
              importId: importItem.importId,
            });
          }
        }
        newInputRows.push(
          createDataForSelectedInputTable(
            itemLabel,
            importItem.brand,
            0,
            importItem.unitType,
            importItem.unitType,
            1.0,
            true,
            importItem.importId,
            importItem.qty,
            catId
          )
        );
      } else {
        newOutputRows.push(
          createDataForSelectedOutputTable(
            itemLabel,
            importItem.brand,
            0,
            importItem.unitType,
            importItem.unitType,
            1.0,
            importItem.importId,
            importItem.qty,
            catId
          )
        );
      }
    }

    setSelectedInputRows(newInputRows);
    setSelectedOutputRows(newOutputRows);
    setInputItemPrices(newPrices);
    setMissingPriceItems(newMissingItems);
    setCategoryPopupSelectedItems([]);
  };

  const [selectedInputItems, setSelectedInputItems] = React.useState([]);
  const [selectedInputRows, setSelectedInputRows] = React.useState([]);
  const [selectedOutputRows, setSelectedOutputRows] = React.useState([]);
  const [selectedProduct, setSelectedProduct] = React.useState([]);
  const [rows, setRows] = React.useState([]);

  const [openSubOrderPersonTable, setOpenSubOrderPersonTable] = useState(false);
  const [selectedPersons, setSelectedPersons] = useState([]);
  const [subOrderPersonData, setSubOrderPersonData] = useState(null);
  const [personRows, setPersonRows] = useState([]);

  const [writeable_page, set_writeable_Page] = React.useState(0);
  const [writeable_rowsPerPage, set_writeable_RowsPerPage] = React.useState(100);

  const handleChange = (name, val) => {
    setValues({ ...values, [name]: val });
    if (error[name]) {
      setError({ ...error, [name]: "" });
    }
  };

  // Recompute material cost whenever input rows or prices change (categories have no price)
  useEffect(() => {
    let total = 0;
    for (const row of selectedInputRows) {
      if (row.importId == null) continue;
      const price = inputItemPrices[row.importId];
      if (price != null) {
        const qty = parseFloat(row.qty) || 0;
        const units = parseFloat(row.itemUintsPerOrderUnit) || 0;
        total += qty * units * price;
      }
    }
    setMaterialCost(parseFloat(total.toFixed(2)));
  }, [selectedInputRows, inputItemPrices]);

  const totalCost =
    materialCost +
    parseFloat(values._labourCost || 0) +
    parseFloat(values._transportCost || 0);

  const handleOutputRowChange = (updatedOutputRows) => {
    setSelectedOutputRows(updatedOutputRows);
  };

  const selectedColumnsForInput = [
    { id: "item", label: "Item", minWidth: 100, editable: false },
    { id: "brand", label: "Brand", minWidth: 100, editable: false },
    {
      id: "qty",
      label: "Qty",
      minWidth: 100,
      editable: true,
      type: "number",
      isDecimal: false,
    },
    { id: "orderUnit", label: "Order Unit", minWidth: 100, editable: false },
    { id: "unitType", label: "Item Unit", minWidth: 100, editable: false },
    {
      id: "itemUintsPerOrderUnit",
      label: "Item Units Amount",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: true,
    },
    {
      id: "isAvailable",
      label: "Availability",
      minWidth: 100,
      editable: true,
      type: "checkbox",
      isDecimal: true,
    },
  ];

  const selectedColumnsForOutput = [
    { id: "item", label: "Item", minWidth: 100, editable: false },
    { id: "brand", label: "Brand", minWidth: 100, editable: false },
    {
      id: "qty",
      label: "Qty",
      minWidth: 100,
      editable: true,
      type: "number",
      isDecimal: false,
    },
    { id: "orderUnit", label: "Order Unit", minWidth: 100, editable: false },
    { id: "unitType", label: "Item Unit", minWidth: 100, editable: false },
    {
      id: "itemUintsPerOrderUnit",
      label: "Item Units Amount",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: true,
    },
  ];

  const handleOpenInputTable = async () => {
    setOpenInputTable(true);
  };

  const handleCloseInputTable = async () => {
    setOpenInputTable(false);
    let subProduct = await getSubOrderProductById(selectedInputItems[0]);
    if (subProduct === "Something went wrong...") {
      setError({ ...error, _inputItems: "Unable to fetch sub order product..." });
      setSelectedInputItems([]);
      setSelectedInputRows([]);
      return;
    }

    setSelectedProduct(subProduct);
    const orderItemList = subProduct.subOrderItems;
    const inputRows = [];
    const outputRows = [];
    const originalQties = [];
    const prices = {};
    const missingItems = [];

    for (let x = 0; x < orderItemList.length; x++) {
      const lineItem = orderItemList[x];

      // ── Category item ────────────────────────────────────────────────────
      if (lineItem.importCategory != null) {
        const row = {
          item: lineItem.importCategory.category,
          brand: "",
          importId: null,
          importCategoryId: lineItem.importCategory.cat_id,
          qty: lineItem.itemQty,
          orderUnit: lineItem.itemUnit,
          unitType: lineItem.itemUnit,
          itemUintsPerOrderUnit: lineItem.prodUnitsForItemUnit,
          existingQty: null,
          isAvailable: true,
        };
        if (lineItem.itemType === "INPUT") {
          inputRows.push(row);
          originalQties.push(lineItem.itemQty);
        } else {
          outputRows.push(row);
        }
        continue;
      }

      // ── Import item ──────────────────────────────────────────────────────
      let importItem = await getImportItemById(lineItem.imports.importId);

      if (lineItem.itemType === "INPUT") {
        const priceData = await getCurrentPrice(importItem.importId);
        if (priceData && priceData !== "Something went wrong..." && priceData.price != null) {
          prices[importItem.importId] = priceData.price;
        } else {
          prices[importItem.importId] = null;
          missingItems.push({
            name: importItem.itemName + " (" + importItem.product_code + ")",
            importId: importItem.importId,
          });
        }

        inputRows.push(
          createDataForSelectedInputTable(
            importItem.itemName + "(" + importItem.product_code + ")",
            importItem.brand,
            lineItem.itemQty,
            lineItem.itemUnit,
            importItem.unitType,
            lineItem.prodUnitsForItemUnit,
            true,
            importItem.importId,
            importItem.qty
          )
        );
        originalQties.push(lineItem.itemQty);
      } else {
        outputRows.push(
          createDataForSelectedOutputTable(
            importItem.itemName + "(" + importItem.product_code + ")",
            importItem.brand,
            lineItem.itemQty,
            lineItem.itemUnit,
            importItem.unitType,
            lineItem.prodUnitsForItemUnit,
            importItem.importId,
            importItem.qty
          )
        );
      }
    }

    setSelectedInputRows(inputRows);
    setSelectedOutputRows(outputRows);
    setOriginalInputQties(originalQties);
    setInputItemPrices(prices);
    setMissingPriceItems(missingItems);
  };

  function createDataForSelectedInputTable(
    item, brand, qty, orderUnit, unitType,
    itemUintsPerOrderUnit, isAvailable, importId, existingQty, fromCategoryId
  ) {
    return { item, brand, qty, orderUnit, unitType, itemUintsPerOrderUnit, isAvailable, importId, existingQty, fromCategoryId };
  }

  function createDataForSelectedOutputTable(
    item, brand, qty, orderUnit, unitType,
    itemUintsPerOrderUnit, importId, existingQty, fromCategoryId
  ) {
    return { item, brand, qty, orderUnit, unitType, itemUintsPerOrderUnit, importId, existingQty, fromCategoryId };
  }

  const submitValue = async () => {
    const validationErrors = SubOrderValidation(
      values,
      selectedInputRows,
      selectedOutputRows,
      subOrderPersonData
    );

    setError(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const subOrderItems = [];

      for (let x = 0; x < selectedInputRows.length; x++) {
        const row = selectedInputRows[x];
        if (row.importCategoryId != null) {
          subOrderItems.push({
            productId: selectedProduct.subProductId,
            importCategoryId: row.importCategoryId,
            orderQty: 0,
            productQty: 0,
            isAvailable: true,
            lineItemType: "INPUT",
            itemUnit: "",
            prodUnitsForItemUnit: 1.0,
          });
        } else if (row.importId != null) {
          subOrderItems.push({
            productId: selectedProduct.subProductId,
            orderQty: parseFloat(row.qty),
            productQty: parseFloat(row.existingQty),
            isAvailable: row.isAvailable,
            lineItemType: "INPUT",
            importId: row.importId,
            itemUnit: row.unitType,
            prodUnitsForItemUnit: parseFloat(row.itemUintsPerOrderUnit),
          });
        }
      }

      for (let x = 0; x < selectedOutputRows.length; x++) {
        const row = selectedOutputRows[x];
        if (row.importCategoryId != null) {
          subOrderItems.push({
            productId: selectedProduct.subProductId,
            importCategoryId: row.importCategoryId,
            orderQty: 0,
            productQty: 0,
            isAvailable: true,
            lineItemType: "OUTPUT",
            itemUnit: "",
            prodUnitsForItemUnit: 1.0,
          });
        } else if (row.importId != null) {
          subOrderItems.push({
            productId: selectedProduct.subProductId,
            orderQty: parseFloat(row.qty),
            productQty: parseFloat(row.existingQty),
            isAvailable: false,
            lineItemType: "OUTPUT",
            importId: row.importId,
            itemUnit: row.unitType,
            prodUnitsForItemUnit: parseFloat(row.itemUintsPerOrderUnit),
          });
        }
      }

      const labourCost = parseFloat(values._labourCost || 0);
      const transportCost = parseFloat(values._transportCost || 0);

      const orderBody = {
        subOrderPersonId: subOrderPersonData.personId,
        cost: parseFloat(values._cost),
        materialCost: materialCost,
        labourCost: labourCost,
        transportCost: transportCost,
        updateMaterialPrice: updateMaterialPrice,
        lineItems: subOrderItems,
        note: values._note,
        orderDate: selectedOrderDate ? selectedOrderDate.toDate() : addedDate.toDate(),
        addedDate: addedDate.toDate(),
      };

      try {
        let result = await addSubOrder(orderBody);
        if (result) {
          resetValues();
          setAlertData({ type: "success", message: "Sub Order Created Successfully..." });
          setAlert(true);
        } else {
          setAlertData({ type: "error", message: "Failed to create sub order..." });
          setAlert(true);
        }
      } catch (error) {
        setAlertData({ type: "error", message: "Something went wrong..." });
        setAlert(true);
      }
    } else {
      setAlertData({
        type: "error",
        message: "Please fix the validation errors before submitting",
      });
      setAlert(true);
    }
  };

  const handleOpenSubOrderPersonTable = async () => {
    setOpenSubOrderPersonTable(true);
  };

  const handleCloseSubOrderPersonTable = async () => {
    setOpenSubOrderPersonTable(false);
    if (selectedPersons.length > 0) {
      try {
        let subOrderPerson = await getSubOrderPersonById(selectedPersons[0]);
        if (subOrderPerson === "Something went wrong...") {
          setError({ ...error, _subOrderPerson: "Unable to fetch sub order person..." });
        } else {
          setSubOrderPersonData(subOrderPerson);
          if (error._subOrderPerson) {
            setError({ ...error, _subOrderPerson: "" });
          }
        }
      } catch (err) {
        setError({ ...error, _subOrderPerson: "Error fetching sub order person..." });
      }
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={11} sx={12}>
        <Typography mt={1} variant="h6">
          Add Sub Order
        </Typography>
      </Grid>
      <Grid item xs={12} sm={1} sx={12}>
        <Tooltip title="Close">
          <IconButton onClick={() => setOpenForm(false)} aria-label="close" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Grid>

      {/* Person selector */}
      <Grid item xs={12} sm={9} sx={12}>
        <SelectButton
          startIcon={<PersonIcon />}
          onClick={handleOpenSubOrderPersonTable}
          style={{ borderColor: error._subOrderPerson ? "#f44336" : undefined }}
        >
          {selectedPersons.length > 0
            ? subOrderPersonData?.personName + " (" + subOrderPersonData?.phone + ")"
            : "Select Person"}
        </SelectButton>
        {error._subOrderPerson && (
          <Typography variant="caption" style={{ color: "red", fontSize: 12, display: "block", marginTop: 4 }}>
            {error._subOrderPerson}
          </Typography>
        )}
      </Grid>

      {/* Sub order product selector */}
      <Grid item xs={12} sm={4} sx={12}>
        <Button
          onClick={handleOpenInputTable}
          variant="outlined"
          style={{ borderColor: error._inputItems ? "#f44336" : undefined }}
        >
          Select Sub Order Product
        </Button>
      </Grid>

      {/* Output items */}
      <Grid item xs={12} sm={12} sx={12}>
        {selectedOutputRows.filter((r) => r.importId != null).length > 0 && (
          <>
            <Typography mt={2} variant="body1">Output Items</Typography>
            <BillWritableTable
              columns={selectedColumnsForOutput}
              rows={selectedOutputRows.filter((r) => r.importId != null)}
              setRows={(updated) =>
                handleOutputRowChange([
                  ...updated,
                  ...selectedOutputRows.filter((r) => r.importCategoryId != null),
                ])
              }
              page={writeable_page}
              setPage={set_writeable_Page}
              rowsPerPage={writeable_rowsPerPage}
              tablePagin={false}
              setRowsPerPage={set_writeable_RowsPerPage}
            />
          </>
        )}
        {selectedOutputRows.filter((r) => r.importCategoryId != null).length > 0 && (
          <>
            <Typography mt={2} variant="body1">Output Categories</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell align="right"><strong>Select Items</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedOutputRows.filter((r) => r.importCategoryId != null).map((row) => (
                  <TableRow key={row.importCategoryId}>
                    <TableCell>{row.item}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Select items from this category">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenCategoryItemSelect(row.importCategoryId, row.item, "OUTPUT")}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
        {error._outputItems && (
          <Typography variant="caption" style={{ color: "red", fontSize: 14, fontWeight: "bold" }}>
            {error._outputItems}
          </Typography>
        )}
      </Grid>

      {/* Input items */}
      <Grid item xs={12} sm={12} sx={12}>
        {selectedInputRows.filter((r) => r.importId != null).length > 0 && (
          <>
            <Typography mt={2} variant="body1">Input Items</Typography>
            <BillWritableTable
              columns={selectedColumnsForInput}
              rows={selectedInputRows.filter((r) => r.importId != null)}
              setRows={(updated) =>
                setSelectedInputRows([
                  ...updated,
                  ...selectedInputRows.filter((r) => r.importCategoryId != null),
                ])
              }
              page={writeable_page}
              setPage={set_writeable_Page}
              rowsPerPage={writeable_rowsPerPage}
              tablePagin={false}
              setRowsPerPage={set_writeable_RowsPerPage}
            />
          </>
        )}
        {selectedInputRows.filter((r) => r.importCategoryId != null).length > 0 && (
          <>
            <Typography mt={2} variant="body1">Input Categories</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Category</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedInputRows.filter((r) => r.importCategoryId != null).map((row) => (
                  <TableRow key={row.importCategoryId}>
                    <TableCell>{row.item}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Select items from this category">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenCategoryItemSelect(row.importCategoryId, row.item, "INPUT")}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
        {error._inputItems && (
          <Typography variant="caption" style={{ color: "red", fontSize: 14, fontWeight: "bold" }}>
            {error._inputItems}
          </Typography>
        )}
      </Grid>

      {/* Missing price warning */}
      {missingPriceItems.length > 0 && (
        <Grid item xs={12} sm={12} sx={12}>
          <MissingPriceWarning items={missingPriceItems} />
        </Grid>
      )}

      {/* Cost section */}
      {selectedProduct != null && (
        <Grid item xs={12} sm={12} sx={12}>
          <CostDisplayBox>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Cost Breakdown
            </Typography>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Material Cost</Typography>
              <Typography variant="body2" fontWeight="medium">
                {materialCost.toFixed(2)}
                {missingPriceItems.length > 0 && (
                  <Typography component="span" variant="caption" color="warning.main" ml={0.5}>
                    (partial)
                  </Typography>
                )}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Labour Cost</Typography>
              <Typography variant="body2">{parseFloat(values._labourCost || 0).toFixed(2)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Transport Cost</Typography>
              <Typography variant="body2">{parseFloat(values._transportCost || 0).toFixed(2)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mt={0.5} pt={0.5} borderTop="1px solid" borderColor="divider">
              <Typography variant="body2" fontWeight="bold">Total Cost</Typography>
              <Typography variant="body2" fontWeight="bold">{totalCost.toFixed(2)}</Typography>
            </Box>
          </CostDisplayBox>
        </Grid>
      )}

      {/* Date fields */}
      <Grid item xs={12} sm={3} sx={12}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Added Date"
            value={addedDate}
            disabled
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} sm={3} sx={12}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Order Date"
            value={selectedOrderDate}
            onChange={(date) => setSelectedOrderDate(date)}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
        </LocalizationProvider>
      </Grid>

      {/* Order cost input */}
      <Grid item xs={12} sm={3} sx={12}>
        <InputField
          name="_cost"
          value={values._cost}
          onChange={(event, newInputValue) => handleChange(event, newInputValue)}
          type="number"
          label="Order Cost"
          errorMsg={error._cost}
        />
      </Grid>

      {/* Labour cost input */}
      <Grid item xs={12} sm={3} sx={12}>
        <InputField
          name="_labourCost"
          value={values._labourCost}
          onChange={(event, newInputValue) => handleChange(event, newInputValue)}
          type="number"
          label="Labour Cost"
          errorMsg={error._labourCost}
        />
      </Grid>

      {/* Transport cost input */}
      <Grid item xs={12} sm={3} sx={12}>
        <InputField
          name="_transportCost"
          value={values._transportCost}
          onChange={(event, newInputValue) => handleChange(event, newInputValue)}
          type="number"
          label="Transport Cost"
          errorMsg={error._transportCost}
        />
      </Grid>

      {/* Note */}
      <Grid item xs={12} sm={12} sx={12}>
        <InputField
          name="_note"
          value={values._note}
          onChange={(event, newInputValue) => handleChange(event, newInputValue)}
          type="text"
          label="Note"
          multiline={true}
        />
      </Grid>

      {/* Update material price flag */}
      <Grid item xs={12} sm={12} sx={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={updateMaterialPrice}
              onChange={(e) => setUpdateMaterialPrice(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              Update output product price on approval
            </Typography>
          }
        />
      </Grid>

      <Grid item xs={12} sm={12} sx={12}>
        {displayAlert ? <FormAlert type={alertData.type} message={alertData.message} /> : null}
      </Grid>

      <Grid item xs={12} sm={12} sx={12}>
        <Grid container justifyContent="space-between">
          <Grid item xs={12} sm={4} sx={12}>
            <Button fullWidth variant="contained" onClick={submitValue}>
              Create
            </Button>
          </Grid>
          <Grid item xs={12} sm={1} sx={12}>
            <Button onClick={resetValues} variant="outlined">
              Reset
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid>
        {openSubOrderPersonTable ? (
          <SubPersonSelectingPopup
            setOpenPersonTable={setOpenSubOrderPersonTable}
            openPersonTable={openSubOrderPersonTable}
            handleClosePersonTable={handleCloseSubOrderPersonTable}
            setSelectedPerson={setSelectedPersons}
            selectedPerson={selectedPersons}
            isOneChoise={true}
          />
        ) : null}
        {openInputTable ? (
          <SubProductSelectingPopup
            setOpenProductTable={setOpenInputTable}
            openProductTable={openInputTable}
            handleCloseProductTable={handleCloseInputTable}
            setSelectedProduct={setSelectedInputItems}
            selectedProduct={selectedInputItems}
            setRows={setRows}
            rows={rows}
            isOneChoise={true}
          />
        ) : null}
        {openCategoryItemSelect && activeCategoryRow ? (
          <ItemSelectingPopup
            setOpenTable={setOpenCategoryItemSelect}
            openTable={openCategoryItemSelect}
            handleCloseTable={handleCloseCategoryItemSelect}
            setSelectedItems={setCategoryPopupSelectedItems}
            selectedItems={categoryPopupSelectedItems}
            rows={categoryPopupRows}
            setRows={setCategoryPopupRows}
            isOneChoise={false}
            presetCategoryIds={[activeCategoryRow.catId]}
          />
        ) : null}
      </Grid>
    </Grid>
  );
}

export default AddSubOrder;
